import React, { useState } from 'react';
import { X, Copy, Check, Twitter, MessageCircle, Mail, Send, Sparkles } from 'lucide-react';
import { buildShareUrl } from '../utils/attribution';

/**
 * <SharePracticeModal>
 * Post-session share modal. User has just finished practising with an AvA;
 * we offer them a frictionless way to share that AvA with a friend.
 *
 * Each share URL:
 *   - Lands on /conversations/practice/<ava-slug>
 *   - Carries utm_source / utm_medium / utm_campaign for attribution
 *   - Triggers the dynamic /api/og/practice-<slug>.png OG card on link unfurl
 *
 * Channels: copy-link, WhatsApp, X (Twitter), iMessage / SMS, Email.
 */

const SharePracticeModal = ({ open, onClose, avaName, avaSlug }) => {
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const safeSlug = (avaSlug || (avaName || 'ava').toLowerCase()).replace(/[^a-z0-9-]+/g, '-');
  const shareTitle = `Practice with ${avaName || 'AvA'} on HiBFF`;
  const shareText = `I've been using HiBFF to rehearse difficult conversations with AvA before they happen IRL. ${avaName ? `Try practising with ${avaName}` : 'Give it a go'} — no card, free to start:`;

  const url = buildShareUrl({
    path: `/conversations/practice/${safeSlug}`,
    source: 'practice-share',
    medium: 'share-modal',
    campaign: 'organic-share',
    content: safeSlug,
  });

  const channelUrl = (channel) => {
    switch (channel) {
      case 'twitter':
        return buildShareUrl({ path: `/conversations/practice/${safeSlug}`, source: 'twitter', medium: 'social', campaign: 'organic-share', content: safeSlug });
      case 'whatsapp':
        return buildShareUrl({ path: `/conversations/practice/${safeSlug}`, source: 'whatsapp', medium: 'social', campaign: 'organic-share', content: safeSlug });
      case 'sms':
        return buildShareUrl({ path: `/conversations/practice/${safeSlug}`, source: 'sms', medium: 'social', campaign: 'organic-share', content: safeSlug });
      case 'email':
        return buildShareUrl({ path: `/conversations/practice/${safeSlug}`, source: 'email', medium: 'social', campaign: 'organic-share', content: safeSlug });
      default:
        return url;
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // older browsers
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  // Native share where supported (mobile, Safari) — falls back to channel chips otherwise
  const tryNativeShare = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url });
      } catch (_) { /* user cancelled */ }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" data-testid="share-practice-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative">
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="share-close-btn"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X size={20} />
        </button>

        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mb-3">
          <Sparkles size={20} className="text-purple-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-1">Share {avaName || 'this practice'} with a friend</h3>
        <p className="text-sm text-gray-600 mb-5">
          Most people only learn HiBFF exists because someone they know shared it.
          Help us reach the next teen, parent, or colleague who needs it.
        </p>

        {/* Copy link */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center gap-2 mb-4" data-testid="share-link-box">
          <input
            value={url}
            readOnly
            className="flex-1 bg-transparent text-xs font-mono text-gray-700 outline-none"
            data-testid="share-link-input"
          />
          <button
            onClick={copyLink}
            data-testid="share-copy-btn"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              copied ? 'bg-emerald-500 text-white' : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
          >
            {copied ? <span className="inline-flex items-center gap-1"><Check size={12} /> Copied</span> : <span className="inline-flex items-center gap-1"><Copy size={12} /> Copy</span>}
          </button>
        </div>

        {/* Channel chips */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
          <ChannelButton
            label="WhatsApp"
            href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${channelUrl('whatsapp')}`)}`}
            color="#25D366"
            icon={<MessageCircle size={16} />}
            testId="share-whatsapp-btn"
          />
          <ChannelButton
            label="X / Twitter"
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(channelUrl('twitter'))}`}
            color="#1A1209"
            icon={<Twitter size={16} />}
            testId="share-twitter-btn"
          />
          <ChannelButton
            label="SMS"
            href={`sms:?&body=${encodeURIComponent(`${shareText} ${channelUrl('sms')}`)}`}
            color="#7C3AED"
            icon={<Send size={16} />}
            testId="share-sms-btn"
          />
          <ChannelButton
            label="Email"
            href={`mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`${shareText}\n\n${channelUrl('email')}`)}`}
            color="#2563EB"
            icon={<Mail size={16} />}
            testId="share-email-btn"
          />
        </div>

        {/* Native share (mobile) */}
        {typeof navigator !== 'undefined' && navigator.share && (
          <button
            onClick={tryNativeShare}
            data-testid="share-native-btn"
            className="w-full py-2.5 rounded-full bg-gray-900 text-white text-sm font-semibold hover:bg-black transition mb-3"
          >
            Share via device…
          </button>
        )}

        <button
          onClick={onClose}
          data-testid="share-skip-btn"
          className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

const ChannelButton = ({ label, href, color, icon, testId }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className="flex flex-col items-center gap-1 py-3 rounded-xl border border-gray-200 hover:border-purple-300 hover:shadow transition"
    style={{ color }}
  >
    {icon}
    <span className="text-[11px] font-semibold">{label}</span>
  </a>
);

export default SharePracticeModal;
